import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Clock, AlertCircle, Building2, DollarSign,
  CreditCard, TrendingUp, ShieldCheck, Loader2, ArrowLeft,
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { CURRENCY_SYMBOL, formatCurrency } from "@/lib/currency";
import { toast } from "sonner";

const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

const PLAN_LABELS: Record<string, string> = {
  basic: "Starter",
  standard: "Growth",
  premium: "Pro",
  free: "مجاني",
  enterprise: "مؤسسي",
};

const STATUS_META: Record<string, { label: string; cls: string; icon: any }> = {
  paid:    { label: "مدفوع ✓",   cls: "bg-green-100 text-green-800 border-green-200",     icon: CheckCircle2 },
  unpaid:  { label: "لم يُدفع",   cls: "bg-amber-100 text-amber-800 border-amber-200",     icon: Clock },
  overdue: { label: "متأخر",     cls: "bg-destructive/10 text-destructive border-destructive/30", icon: AlertCircle },
};

const AccountantDashboard = () => {
  const { profile, user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  const { data: nurseries } = useQuery({
    queryKey: ["accountant-nurseries"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("id, name, address")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: subscriptions } = useQuery({
    queryKey: ["accountant-subs-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscriptions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: subInvoices } = useQuery({
    queryKey: ["accountant-sub-invoices-dash"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .select("id, nursery_id, month, year, amount, status, due_date, paid_at, confirmed_by, confirmed_at")
        .order("year", { ascending: false })
        .order("month", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const confirmCollection = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          confirmed_by: user?.id ?? null,
          confirmed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تأكيد التحصيل ✓");
      qc.invalidateQueries({ queryKey: ["accountant-sub-invoices-dash"] });
    },
    onError: (e: any) => toast.error(e?.message || "فشل تأكيد التحصيل"),
  });

  // Build a per-nursery summary
  const perNursery = useMemo(() => {
    if (!nurseries) return [];
    return nurseries.map((n: any) => {
      const subs = (subscriptions || []).filter((s: any) => s.nursery_id === n.id);
      const activeSub = subs.find((s: any) => s.status === "active") || subs[0];
      const invs = (subInvoices || []).filter((i: any) => i.nursery_id === n.id);
      const currentInv = invs.find((i: any) => i.month === currentMonth && i.year === currentYear);
      const lastPaidInv = invs.find((i: any) => i.status === "paid");
      const pendingCount = invs.filter((i: any) => i.status !== "paid").length;
      return { nursery: n, sub: activeSub, currentInv, lastPaidInv, pendingCount };
    });
  }, [nurseries, subscriptions, subInvoices, currentMonth, currentYear]);

  // Top stats
  const paidThisMonth = perNursery.filter((p) => p.currentInv?.status === "paid").length;
  const unpaidThisMonth = perNursery.filter((p) => p.currentInv && p.currentInv.status !== "paid").length;
  const noInvoiceCount = perNursery.filter((p) => !p.currentInv).length;
  const collected = (subInvoices || [])
    .filter((i: any) => i.status === "paid" && i.year === currentYear && i.month === currentMonth)
    .reduce((s: number, i: any) => s + Number(i.amount || 0), 0);
  const outstanding = (subInvoices || [])
    .filter((i: any) => i.status !== "paid")
    .reduce((s: number, i: any) => s + Number(i.amount || 0), 0);

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">مرحباً، {profile?.full_name || "المحاسب"} 💼</h1>
          <p className="text-muted-foreground text-sm mt-1">
            متابعة اشتراكات الحضانات لشهر {MONTHS_AR[currentMonth - 1]} {currentYear} — كل حضانة على حدة
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => navigate("/accountant/subscription-invoices")} className="gap-1">
          فواتير الاشتراكات الكاملة <ArrowLeft className="w-3 h-3" />
        </Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-r-4 border-r-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">حضانات دفعت هذا الشهر</p>
              <p className="text-2xl font-bold text-green-600">{paidThisMonth}</p>
            </div>
            <CheckCircle2 className="w-8 h-8 text-green-500/30" />
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">لم تدفع بعد</p>
              <p className="text-2xl font-bold text-amber-600">{unpaidThisMonth}</p>
            </div>
            <Clock className="w-8 h-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-primary">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">محصّل هذا الشهر</p>
              <p className="text-xl font-bold text-primary">
                {collected.toLocaleString("ar-EG")} <span className="text-xs">{CURRENCY_SYMBOL}</span>
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-primary/30" />
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-destructive">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">إجمالي مستحق</p>
              <p className="text-xl font-bold text-destructive">
                {outstanding.toLocaleString("ar-EG")} <span className="text-xs">{CURRENCY_SYMBOL}</span>
              </p>
            </div>
            <AlertCircle className="w-8 h-8 text-destructive/30" />
          </CardContent>
        </Card>
      </div>

      {/* Per-Nursery Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Building2 className="w-5 h-5" /> الحضانات ({nurseries?.length || 0})
          </h2>
          {noInvoiceCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {noInvoiceCount} حضانة بدون فاتورة لهذا الشهر
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {perNursery.map(({ nursery, sub, currentInv, lastPaidInv, pendingCount }) => {
            const status = currentInv?.status || "missing";
            const meta = STATUS_META[status];
            const Icon = meta?.icon;
            return (
              <Card key={nursery.id} className="overflow-hidden hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="text-base truncate">{nursery.name}</CardTitle>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {sub ? PLAN_LABELS[sub.plan] || sub.plan : "بدون اشتراك"}
                        {sub?.billing_cycle === "yearly" ? " · سنوي" : sub ? " · شهري" : ""}
                      </p>
                    </div>
                    {meta ? (
                      <Badge variant="outline" className={`gap-1 ${meta.cls}`}>
                        {Icon && <Icon className="w-3 h-3" />}
                        {meta.label}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-muted text-muted-foreground">لا فاتورة</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  {/* Current month line */}
                  <div className="rounded-lg border p-3 bg-muted/30">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>اشتراك {MONTHS_AR[currentMonth - 1]} {currentYear}</span>
                      {currentInv?.due_date && (
                        <span>استحقاق {format(new Date(currentInv.due_date), "dd MMM", { locale: ar })}</span>
                      )}
                    </div>
                    {currentInv ? (
                      <div className="flex items-center justify-between">
                        <span className="text-base font-bold">{formatCurrency(currentInv.amount)}</span>
                        {currentInv.status === "paid" ? (
                          <span className="text-[11px] text-green-700 flex items-center gap-1">
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {currentInv.confirmed_at
                              ? `مؤكد ${format(new Date(currentInv.confirmed_at), "dd MMM", { locale: ar })}`
                              : currentInv.paid_at
                                ? `تم الدفع ${format(new Date(currentInv.paid_at), "dd MMM", { locale: ar })}`
                                : "مدفوع"}
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            className="h-7 gap-1"
                            disabled={confirmCollection.isPending}
                            onClick={() => {
                              if (window.confirm(`تأكيد تحصيل اشتراك ${nursery.name} لهذا الشهر؟`)) {
                                confirmCollection.mutate(currentInv.id);
                              }
                            }}
                          >
                            {confirmCollection.isPending && confirmCollection.variables === currentInv.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3" />
                            )}
                            تأكيد التحصيل
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">لم يتم إنشاء فاتورة لهذا الشهر بعد.</p>
                    )}
                  </div>

                  {/* Footer info */}
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CreditCard className="w-3 h-3" />
                      آخر دفعة: {lastPaidInv?.paid_at
                        ? format(new Date(lastPaidInv.paid_at), "dd MMM yyyy", { locale: ar })
                        : "لا يوجد"}
                    </span>
                    {pendingCount > 0 && (
                      <span className="flex items-center gap-1 text-amber-700">
                        <TrendingUp className="w-3 h-3" />
                        {pendingCount} فاتورة معلقة
                      </span>
                    )}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full h-7 text-xs gap-1"
                    onClick={() => navigate(`/accountant/subscription-invoices?nursery=${nursery.id}`)}
                  >
                    عرض كل فواتير الحضانة <ArrowLeft className="w-3 h-3" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
          {!nurseries?.length && (
            <p className="col-span-full text-center py-6 text-muted-foreground text-sm">لا توجد حضانات</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountantDashboard;
